import { useState } from "react";
import { Modal, Form, Input, notification } from "antd";
import { useCreateOrganizationMutation } from "organizations/queries";

type Props = {
  open: boolean;
  onClose: () => void;
};

export const CreateOrganizationModal = ({ open, onClose }: Props) => {
  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();
  const createMutation = useCreateOrganizationMutation();

  const handleOk = () => {
    form.submit();
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: {
    name: string;
    description?: string;
    projectPrefix?: string;
  }) => {
    try {
      await createMutation.mutateAsync({
        name: values.name.trim(),
        description: values.description?.trim(),
        projectPrefix: values.projectPrefix?.trim(),
      });
      api.success({
        duration: 5,
        message: "Organization Created",
        description: `The organization "${values.name}" has been created.`,
      });
      form.resetFields();
      onClose();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to create organization";
      api.error({
        message: "Error",
        description: message,
      });
    }
  };

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    // Auto-generate prefix suggestion from name if prefix field is empty
    const currentPrefix = form.getFieldValue("projectPrefix");
    if (!currentPrefix) {
      const words = name.replace(/[^a-zA-Z\s]/g, "").trim().split(/\s+/);
      const suggested = words
        .map((w: string) => w.charAt(0).toUpperCase())
        .join("")
        .slice(0, 4);
      if (suggested.length >= 2) {
        form.setFieldValue("projectPrefix", suggested);
      }
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title='Create Organization'
        open={open}
        onOk={handleOk}
        onCancel={handleCancel}
        okText='Create Organization'
        confirmLoading={createMutation.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          name='create-organization-form'
          layout='vertical'
          onFinish={onFinish}
          requiredMark={false}
        >
          <Form.Item
            name='name'
            label='Organization Name'
            rules={[
              {
                required: true,
                message: "Please enter an organization name.",
              },
            ]}
          >
            <Input placeholder='Organization name' onChange={onNameChange} />
          </Form.Item>
          <Form.Item name='description' label='Description'>
            <Input placeholder='Optional description' />
          </Form.Item>
          <Form.Item
            name='projectPrefix'
            label='Project ID Prefix'
            tooltip='A short prefix used for project IDs (e.g., MME-0001). Cannot be changed after creation.'
            rules={[
              {
                required: true,
                message: "Please enter a project ID prefix.",
              },
              {
                pattern: /^[A-Z0-9]{2,10}$/,
                message:
                  "Prefix must be 2-10 uppercase letters or numbers.",
              },
            ]}
          >
            <Input
              placeholder='e.g., MME'
              maxLength={10}
              style={{ textTransform: "uppercase" }}
              onChange={(e) => {
                form.setFieldValue(
                  "projectPrefix",
                  e.target.value.toUpperCase()
                );
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
